import {
    Component,
    EventEmitter,
    inject,
    Input,
    NgZone,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    Output,
    Renderer2,
    type SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import type { Editor, EditorEvent, EditorSettings } from 'tinymce';

import { getUuid } from '@common/helpers/uuid';
import { defaultTinymceConfig } from '@components/sp-editor';
import type { ThemeColor } from '@sites/site-theme/src/types';
import { updateEditorsState } from '@common/helpers/tiny-mce';
import { type BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { DeviceMode, ESelectedEntityType } from '@common/types/layout.type';

import { AiTextPluginComponent, type EditorAiModalModalPayload } from '@sites/ai-text-plugin';
import { SpTextGradientComponent } from '@components/sp-color-picker/src/sp-text-gradient/sp-text-gradient.component';
import { type GradientText } from '@components/sp-color-picker/src/types/color-picker.type';
import { SpEditorService } from '../service/sp-editor.service';
import { EditorComponent } from '@tinymce/tinymce-angular';

@Component({
    selector: 'sp-editor',
    templateUrl: './sp-editor.component.html',
    styleUrls: ['./sp-editor.component.less'],
    imports: [EditorComponent],
})
export class SpEditorComponent implements OnInit, OnChanges, OnDestroy {
    private readonly spEditorService = inject(SpEditorService);

    @Input() public inline: boolean = true;
    @Input() public baseHref!: string;
    @Input() public containerId?: string;
    @Input() public content: string;
    @Input() public toolbarMenu: string;
    @Input() public bindToElement: HTMLElement;
    @Input() public changeContentOnKeyUp: boolean = true;
    @Input() public isPasteAsText: boolean = false;
    @Input() public selectedThemeColors: ThemeColor[] = [];
    @Input() public isEduSite: boolean = false;
    @Input() public selectedDeviceMode: DeviceMode = DeviceMode.desktop;
    @Input() public placeholder: string = '';
    @Input() public inputDebounceTime: number = 200;
    @Input() public useExperimentalDblClickFocus: boolean = false;

    @Output() public changeContent = new EventEmitter<string>();
    @Output() public focusInEvent = new EventEmitter<void>();
    @Output() public focusOutEvent = new EventEmitter<string>();
    @Output() public focusOutWithoutContentEvent = new EventEmitter<void>();
    @Output() public keyUpEvent = new EventEmitter<{ event: EditorEvent<any>; editor: Editor }>();
    @Output() public isEmptyContent = new EventEmitter<boolean>();
    @Output() public editWidgetEvent = new EventEmitter<void>();
    @Output() public editWidgetInNavigationEvent = new EventEmitter<void>();
    @Output() public showTextGradientModal = new EventEmitter<boolean>();
    @Output() public showAiModal = new EventEmitter<boolean>();
    @Output() public changeEditorHeight = new EventEmitter<number>();

    private readonly renderer: Renderer2 = inject(Renderer2);
    private readonly modalService: BsModalService = inject(BsModalService);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly ngZone: NgZone = inject(NgZone);

    public editor: Editor;
    public config: Partial<EditorSettings>;
    public id: string = getUuid();
    public readonly TOOLBAR_HEIGHT = 39;

    private readonly content$ = new Subject<string>();
    private readonly unsubscribe$ = new Subject<void>();
    private modalRef: BsModalRef = null;
    private isBufferPermissionEnabled: boolean = true;

    private lastEditorHeight: number = 0;
    private lastEmittedContent: string | null = null;

    public ngOnInit(): void {
        this.content$.next(this.content);

        this.content$
            .pipe(takeUntil(this.unsubscribe$), debounceTime(this.inputDebounceTime), distinctUntilChanged())
            .subscribe(() => {
                if (this.editor.destroyed) {
                    return;
                }

                this.changeEditorHeightHandler();

                this.emitContentChange(this.editor.getContent());
            });

        this.config = {
            ...defaultTinymceConfig(
                this.translate,
                this.baseHref,
                this.inline,
                this.selectedThemeColors,
                this.toolbarMenu,
                this.isEduSite,
                this.isPasteAsText
            ),
            setup: (editor: Editor): void => {
                this.editor = editor;

                if (!this.isEduSite) {
                    editor.ui.registry.addButton('AIButton', {
                        text: `<div class="toolbar-ai-button">
                                <span class="tox-icon tox-tbtn__icon-wrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.7553 1.24984C11.7786 1.14611 12.7455 1.60156 13.6449 2.49321L13.6467 2.49499C14.5481 3.39326 15.0119 4.36045 14.9131 5.38684C14.8194 6.36045 14.2376 7.13394 13.6576 7.71556V7.71556L13.6569 7.71627L7.60707 13.7932C7.38844 14.0183 7.11331 14.1925 6.87154 14.3137C6.62481 14.4375 6.32665 14.5491 6.03079 14.5938L6.0241 14.5948L3.70427 14.9288L3.70315 14.929C3.01684 15.0286 2.33163 14.8427 1.84206 14.3535C1.35319 13.865 1.16653 13.1811 1.26066 12.4974L1.26108 12.4944L1.58748 10.1759L1.59029 10.1573C1.63702 9.86526 1.74951 9.57027 1.87021 9.32818C1.9902 9.08753 2.16042 8.81459 2.37475 8.59807L2.37675 8.59604L8.43309 2.51259C9.01364 1.9306 9.78446 1.34825 10.7553 1.24984ZM4.5602 12.7849L5.73401 12.6159C5.76443 12.6109 5.85646 12.5854 5.97501 12.5259C6.02975 12.4985 6.07811 12.4697 6.11634 12.4433C6.15652 12.4156 6.1722 12.3999 6.1722 12.3999L6.18459 12.3873L12.2409 6.30379C12.708 5.83559 12.8952 5.47672 12.9223 5.19524C12.9443 4.96624 12.8805 4.55531 12.2358 3.91259C11.5955 3.27807 11.1865 3.21638 10.957 3.23965C10.6749 3.26824 10.3161 3.457 9.84956 3.92454V3.92454L3.79613 10.0051V10.0051C3.79613 10.0051 3.79015 10.0114 3.78164 10.0221C3.77224 10.0339 3.76037 10.0499 3.74665 10.0703C3.71876 10.1117 3.68866 10.1632 3.66007 10.2206C3.60005 10.341 3.57266 10.434 3.56601 10.4685L3.40196 11.6338L3.5602 11.6338C4.11249 11.6338 4.5602 12.0815 4.5602 12.6338L4.5602 12.7849Z" fill="currentColor"/>
                                        <path d="M3.5 1.00006L4.38388 2.61618L6 3.50006L4.38388 4.38394L3.5 6.00006L2.61611 4.38394L0.999997 3.50006L2.61611 2.61618L3.5 1.00006Z" fill="currentColor"/>
                                        <path d="M12.75 10.5L13.5455 11.9545L15 12.75L13.5455 13.5455L12.75 15L11.9545 13.5455L10.5 12.75L11.9545 11.9545L12.75 10.5Z" fill="currentColor"/>
                                    </svg>
                                </span>
                                ${this.translate.instant('ai_button')}
                           </div>`,
                        tooltip: this.translate.instant('ai_tooltip_improve'),
                        onAction: () => {
                            this.ngZone.run(() => {
                                this.showAiModal.emit(true);

                                this.modalRef = this.modalService.show(AiTextPluginComponent, {
                                    initialState: {
                                        text: this.editor.getContent(),
                                    },
                                    class: 'modal-lg',
                                });
                                this.modalRef.content.saveData.subscribe((data: EditorAiModalModalPayload) => {
                                    this.editor.setContent(data.generatedText);
                                    this.emitContentChange(this.editor.getContent());
                                });
                            });
                        },
                        onSetup: () => {
                            return () => {};
                        },
                    });
                    editor.ui.registry.addButton('TextGradient', {
                        text: `<div class="toolbar-gradient-button">
                                <span class="tox-icon tox-tbtn__icon-wrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M4 4H12V6H14V8H12V10H14V12H12V14H14V16H12V18H14V20H12H4V4ZM16 18H14V16H16V18ZM18 18H16V20H18V18ZM18 16H16V14H18V16ZM18 12H20V14H18V12ZM16 12V14H14V12H16ZM16 10V12H18V10H20V8H18V6H20V4H18V6H16V4H14V6H16V8H14V10H16ZM16 10H18V8H16V10ZM18 16H20V18H18V16Z" fill="currentColor"/>
                                    </svg>
                                </span>`,
                        tooltip: this.translate.instant('text_gradient_tooltip'),
                        onAction: () => {
                            this.ngZone.run(() => {
                                this.showTextGradientModal.emit(true);

                                this.modalRef = this.modalService.show(SpTextGradientComponent, {
                                    initialState: {
                                        text: this.editor.selection.getNode(),
                                    },
                                    class: 'text-gradient-modal',
                                });

                                this.modalRef.content.saveData.subscribe((data: GradientText) => {
                                    const gradientCSS = `-webkit-linear-gradient(${data.deg}deg, ${data.values
                                        .map((value) => `${value.color} ${value.position}`)
                                        .join(', ')})`;
                                    let filteredStyle = '';
                                    const spanStyle = `background: ${gradientCSS}; -webkit-background-clip: text; -webkit-text-fill-color: transparent;`;
                                    let blockStyle = spanStyle + ' display: inline-block;';
                                    const selectedNode = this.editor.selection.getNode() as any;
                                    const tagName = selectedNode.tagName;
                                    const selectedNodeClassList = selectedNode.classList;
                                    const selectedNodeText = selectedNode.textContent;
                                    const selectedContent = this.editor.selection.getContent();
                                    const selectedNodes = this.editor.selection.getSelectedBlocks() as any[];

                                    if (selectedNodes.length === 1) {
                                        if (selectedNodeText.length === selectedContent.length) {
                                            if (selectedNode.hasAttribute('style')) {
                                                for (const property of selectedNode.style) {
                                                    if (property !== 'color' && !property.startsWith('background')) {
                                                        const value = selectedNode.style.getPropertyValue(property);
                                                        if (value) {
                                                            filteredStyle += `${property}: ${value}; `;
                                                        }
                                                    }
                                                }

                                                blockStyle += filteredStyle;
                                            }

                                            const styledNode = this.editor.dom.create(
                                                tagName,
                                                {
                                                    style: blockStyle,
                                                    class: selectedNodeClassList.value
                                                        ? selectedNodeClassList.value
                                                        : '',
                                                },
                                                selectedContent
                                            );

                                            this.editor.dom.replace(styledNode, selectedNode);
                                        } else {
                                            const styledSpan = `<span style="${spanStyle}">${selectedContent}</span>`;
                                            this.editor.selection.setContent(styledSpan);
                                        }
                                    } else {
                                        const styledNodes = [];

                                        selectedNodes.forEach((node) => {
                                            const nodeTagName = node.tagName;
                                            const nodeClassList = node.classList;
                                            const nodeContent = node.textContent;
                                            let nodeFilteredStyle = '';
                                            let nodeStyles = `background: ${gradientCSS}; -webkit-background-clip: text; -webkit-text-fill-color: transparent; display: inline-block;`;

                                            for (const property of node.style) {
                                                if (property !== 'color' && !property.startsWith('background')) {
                                                    const value = node.style.getPropertyValue(property);
                                                    if (value) {
                                                        nodeFilteredStyle += `${property}: ${value}; `;
                                                    }
                                                }
                                            }

                                            nodeStyles += nodeFilteredStyle;

                                            const styledNode = this.editor.dom.create(
                                                nodeTagName,
                                                {
                                                    style: nodeStyles,
                                                    class: nodeClassList.value ? nodeClassList.value : '',
                                                },
                                                nodeContent
                                            );

                                            styledNodes.push(styledNode);
                                        });

                                        styledNodes.forEach((styledNode, index) => {
                                            const selectedNode = selectedNodes[index];
                                            if (selectedNode) {
                                                this.editor.dom.replace(styledNode, selectedNode);
                                            }
                                        });
                                    }

                                    this.emitContentChange(this.editor.getContent());
                                });
                            });
                        },
                        onSetup: () => {
                            return () => {};
                        },
                    });
                    editor.ui.registry.addButton('clearTextStyleCustom', {
                        tooltip: 'Clear text style',
                        icon: 'remove-formatting',
                        onAction: function () {
                            const text = editor
                                .getContent({ format: 'text' })
                                .replace(/<\/p>|<br\s*\/?>/gi, '\n')
                                .replace(/\n/g, '<br>');
                            editor.setContent(`${text}`);
                        },

                        onSetup: () => {
                            return () => {};
                        },
                    });
                    const registryButtons = (): void => {
                        editor.ui.registry.addButton('pasteAsTextCustom', {
                            tooltip: this.isBufferPermissionEnabled
                                ? 'Paste as text'
                                : 'Для использования кнопок вставки текста необходимо включить разрешение на чтение из буфера в настройках вашего браузера',
                            icon: 'paste-text',
                            onAction: function () {
                                navigator.clipboard
                                    .readText()
                                    .then(function (text) {
                                        editor.execCommand(
                                            'mceInsertContent',
                                            false,
                                            text.replace(/<\/p>|<br\s*\/?>/gi, '\n').replace(/\n/g, '<br>')
                                        );
                                    })
                                    .catch(function (err) {
                                        console.error('Error paste text', err);
                                    });
                            },

                            onSetup: () => {
                                return () => {};
                            },
                        });
                        editor.ui.registry.addButton('pasteTextCustom', {
                            tooltip: this.isBufferPermissionEnabled
                                ? 'Paste'
                                : 'Для использования кнопок вставки текста необходимо включить разрешение на чтение из буфера в настройках вашего браузера',
                            icon: 'paste',
                            onAction: function () {
                                navigator.clipboard
                                    .read()
                                    .then(function (clipboardItems) {
                                        clipboardItems.forEach((item) => {
                                            item.types.forEach((type) => {
                                                if (type === 'text/html') {
                                                    item.getType(type).then((blob) => {
                                                        blob.text().then((html) => {
                                                            editor.execCommand(
                                                                'mceInsertContent',
                                                                true,
                                                                html.replace(/<(\w+)(\s+[^>]*?)?>/g, '<$1>')
                                                            );
                                                        });
                                                    });
                                                }
                                            });
                                        });
                                    })
                                    .catch(function (err) {
                                        console.error('Error paste text', err);
                                    });
                            },
                            onSetup: () => {
                                return () => {};
                            },
                        });
                    };

                    const ua = navigator.userAgent;
                    const isSafari = ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
                    const isFF = ua.toLowerCase().includes('firefox');

                    if (isFF || isSafari) {
                        try {
                            navigator.clipboard
                                .readText()
                                .then(() => {
                                    this.isBufferPermissionEnabled = true;
                                    registryButtons();
                                })
                                .catch(() => {
                                    this.isBufferPermissionEnabled = false;
                                    registryButtons();
                                });
                        } catch (error) {
                            this.isBufferPermissionEnabled = false;
                        }
                    } else {
                        try {
                            navigator.permissions
                                .query(<PermissionDescriptor>(<unknown>{ name: 'clipboard-read' }))
                                .then((permission) => {
                                    this.isBufferPermissionEnabled = permission.state !== 'denied';
                                    registryButtons();
                                });
                        } catch (error) {
                            this.isBufferPermissionEnabled = false;
                        }
                    }
                }

                if (!this.isEduSite) {
                    editor.ui.registry.addButton('EditWidget', {
                        text: `<div class="toolbar-edit-button">
                                <span class="tox-icon tox-tbtn__icon-wrap">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="currentColor">
                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.7553 1.24978C11.7786 1.14605 12.7455 1.6015 13.6449 2.49315L13.6467 2.49493C14.5481 3.3932 15.0119 4.36039 14.9131 5.38678C14.8194 6.36039 14.2376 7.13387 13.6576 7.71549C13.6576 7.71551 13.6576 7.71548 13.6576 7.71549C13.6574 7.71571 13.6571 7.71599 13.6569 7.71621L12.0736 9.30662C12.0733 9.30686 12.0731 9.3071 12.0728 9.30733L7.60708 13.7931C7.38844 14.0182 7.11331 14.1924 6.87154 14.3137C6.62481 14.4374 6.32666 14.5491 6.03079 14.5937L6.0241 14.5947L3.70428 14.9287C3.7039 14.9288 3.70353 14.9289 3.70315 14.9289C3.01684 15.0285 2.33164 14.8427 1.84206 14.3534C1.35319 13.8649 1.16653 13.1811 1.26067 12.4973L1.26109 12.4943L1.58749 10.1758L1.59029 10.1572C1.63703 9.8652 1.74951 9.57021 1.87021 9.32812C1.9902 9.08747 2.16042 8.81453 2.37475 8.59801L2.37676 8.59598L7.28027 3.67051C7.2871 3.66344 7.29404 3.65648 7.30107 3.64962L8.43309 2.51252C9.01365 1.93054 9.78446 1.34819 10.7553 1.24978ZM7.23603 6.54972L3.79613 10.005C3.79615 10.005 3.79625 10.0049 3.79613 10.005C3.79524 10.006 3.79015 10.0114 3.78164 10.022C3.77224 10.0338 3.76038 10.0498 3.74665 10.0702C3.71876 10.1117 3.68867 10.1632 3.66007 10.2205C3.60005 10.3409 3.57266 10.434 3.56602 10.4684L3.24198 12.7701C3.24192 12.7705 3.24187 12.7709 3.24182 12.7713C3.23112 12.85 3.2402 12.8969 3.24646 12.9175C3.25054 12.9309 3.25439 12.9369 3.25575 12.9387C3.25746 12.9399 3.26267 12.9431 3.27398 12.9465C3.29267 12.9521 3.33799 12.961 3.41613 12.9496L5.73401 12.6158C5.76443 12.6108 5.85646 12.5853 5.97501 12.5259C6.02975 12.4984 6.07812 12.4697 6.11634 12.4433C6.15652 12.4156 6.17281 12.3992 6.1722 12.3999L6.18459 12.3872L9.50445 9.05247C9.06401 8.8063 8.64381 8.48867 8.28306 8.12318C7.85969 7.69426 7.48289 7.16339 7.23603 6.54972ZM11.0122 7.53794C10.6459 7.42627 10.1459 7.16341 9.70646 6.71822C9.19919 6.20429 8.88674 5.56335 8.94254 4.83556L9.84904 3.925C9.84887 3.92517 9.84922 3.92482 9.84904 3.925C10.3156 3.45746 10.6749 3.26818 10.957 3.23958C11.1865 3.21632 11.5955 3.278 12.2358 3.91253C12.8805 4.55525 12.9443 4.96618 12.9223 5.19518C12.8952 5.47666 12.708 5.83553 12.2409 6.30373L11.0122 7.53794Z"/>
                                    </svg>
                                </span>
                                ${this.translate.instant('setting_button')}
                        </div>`,
                        tooltip: this.translate.instant('control_widget_settings'),
                        onAction: () => {
                            this.spEditorService.setEditor(this.editor);

                            this.ngZone.run(() => {
                                this.editWidgetEvent.emit();

                                this.editWidgetInNavigationEvent.emit();
                            });
                        },
                    });
                }

                editor.ui.registry.addGroupToolbarButton('alignment', {
                    icon: 'align-center',
                    tooltip: 'Alignment',
                    items: 'aligncenter alignleft alignright alignjustify',
                });

                editor.ui.registry.addGroupToolbarButton('lists', {
                    icon: 'ordered-list',
                    tooltip: 'Lists',
                    items: 'bullist numlist',
                });

                editor
                    .on('init', () => {
                        if (this.useExperimentalDblClickFocus) {
                            let isFocused = false;
                            const body = editor.getBody();

                            body.addEventListener('mousedown', (e) => {
                                if (isFocused) {
                                    return;
                                }

                                e.preventDefault();
                            });

                            body.addEventListener('blur', () => {
                                isFocused = false;
                            });

                            body.addEventListener('dblclick', (e) => {
                                if (isFocused) {
                                    return;
                                }

                                e.preventDefault();

                                editor.focus();
                                isFocused = true;

                                const doc = editor.getDoc() as Document;
                                const x = (e as MouseEvent).clientX;
                                const y = (e as MouseEvent).clientY;

                                let rng: Range | null = null;

                                // Старый API (WebKit/Blink)
                                const anyDoc = doc as any;
                                if (typeof anyDoc.caretRangeFromPoint === 'function') {
                                    rng = anyDoc.caretRangeFromPoint(x, y) as Range | null;
                                } else if (typeof anyDoc.caretPositionFromPoint === 'function') {
                                    // Новый API (Firefox и др.)
                                    const pos = anyDoc.caretPositionFromPoint(x, y);
                                    if (pos && pos.offsetNode) {
                                        rng = doc.createRange();
                                        rng.setStart(pos.offsetNode, pos.offset ?? 0);
                                        rng.collapse(true);
                                    }
                                }

                                // На крайний случай: ставим каретку в начало узла под курсором
                                if (!rng && e.target) {
                                    const fallback = doc.createRange();
                                    const node = e.target as Node;
                                    fallback.selectNodeContents(node.nodeType === 3 ? node.parentNode! : node);
                                    fallback.collapse(true);
                                    rng = fallback;
                                }

                                if (rng) {
                                    editor.selection.setRng(rng);
                                }
                            });
                        }
                    })
                    .on('focusin', (): void => {
                        this.focusInEvent.emit();
                    })
                    .on('blur', (): void => {
                        this.focusOutWithoutContentEvent.emit();

                        const content = this.editor.getContent();

                        if (content === this.content) {
                            return;
                        }

                        // Emit synchronously instead of going through the debounced pipeline:
                        // with useExperimentalFocus the editor can be destroyed before the
                        // debounce elapses, which would silently drop the last edit.
                        this.emitContentChange(content);

                        this.showTextGradientModal.emit(false);
                        this.showAiModal.emit(false);

                        this.focusOutEvent.emit(content);
                    })
                    .on('keyup', (): void => {
                        if (this.changeContentOnKeyUp) {
                            this.content$.next(this.editor.getContent());
                        }
                    })
                    .on('change', () => {
                        this.changeEditorHeightHandler();

                        if (this.editor.getContent() === this.content) {
                            return;
                        }

                        this.content$.next(this.editor.getContent());
                    })
                    .on('keyup', (event) => {
                        this.keyUpEvent.emit({ event, editor });

                        this.isEmptyContent.emit(this.editor.getContent() === '');
                    })
                    .on('ExecCommand', (evt) => {
                        const cmd = evt.command;
                        const ui = evt.ui as any;

                        if (
                            cmd === 'JustifyLeft' ||
                            cmd === 'JustifyRight' ||
                            cmd === 'JustifyFull' ||
                            cmd === 'JustifyCenter'
                        ) {
                            const node = evt.target.selection.getNode();
                            const DMClasses = node.classList.value
                                .split(' ')
                                .filter((el) => !el.includes(this.selectedDeviceMode));

                            DMClasses.push(`${this.selectedDeviceMode}-text-${cmd}`);
                            node.classList = DMClasses.join(' ').trim();
                            node.style.textAlign = '';
                        }

                        if (cmd === 'mceToggleFormat') {
                            const selectedBlock = evt.target.selection.getSelectedBlocks()[0];
                            const classList = selectedBlock.classList.value.split(' ');

                            if (/\b(1|2|3|4)\b/.test(evt.value) && selectedBlock) {
                                evt.target.dom.setAttrib(selectedBlock, 'class', '');
                            } else {
                                evt.target.dom.setAttrib(selectedBlock, 'class', classList[classList.length - 1]);
                            }
                        }

                        if (cmd === 'FontSize' || cmd === 'mceApplyTextcolor') {
                            const val = evt.value;
                            const node = evt.target.selection.getNode();
                            const nodeParent = node.parentNode;
                            const selectedText = evt.target.selection.getContent().replace(/<\/?[^>]+(>|$)/g, '');
                            const selectedTextBlock = evt.target.selection
                                .getSelectedBlocks()[0]
                                .innerHTML.replace(/<\/?[^>]+(>|$)/g, '');

                            if (selectedText === selectedTextBlock && cmd === 'FontSize') {
                                const tagName = nodeParent.tagName;

                                const requiredGradientStyles = [
                                    'background',
                                    '-webkit-background-clip',
                                    '-webkit-text-fill-color',
                                ];
                                let collectedStyles = '';

                                requiredGradientStyles.forEach((property) => {
                                    const value = nodeParent.style.getPropertyValue(property);
                                    if (value) {
                                        collectedStyles += `${property}: ${value}; `;
                                    }
                                });

                                const color = node.style.color;
                                const bgColor = node.style.backgroundColor;

                                if (this.hasGradientBackground(nodeParent)) {
                                    nodeParent.style.cssText += collectedStyles;
                                } else {
                                    if (color) collectedStyles += `color: ${color};`;
                                    if (bgColor) collectedStyles += `background-color: ${bgColor};`;
                                }

                                const styledNode = editor.dom.create(
                                    tagName,
                                    { style: `font-size: ${val}; ${collectedStyles}` },
                                    selectedText
                                );

                                // editor.dom.replace(styledNode, node);
                                editor.dom.replace(styledNode, nodeParent);
                            }

                            if (node.nodeName === 'A' && val && cmd === 'mceApplyTextcolor') {
                                node.style.color = 'inherit';
                            }

                            if (node.nodeName === 'SPAN' && nodeParent.nodeName === 'LI') {
                                if (cmd === 'FontSize') {
                                    nodeParent.style.fontSize = val;
                                    nodeParent.style.marginLeft = val;
                                } else {
                                    nodeParent.style.color = val;
                                }
                            }

                            if (node.nodeName === 'UL' || node.nodeName === 'OL') {
                                node.childNodes.forEach(
                                    (el: { style: { fontSize: any; marginLeft: any; color: any } }) => {
                                        if (cmd === 'FontSize') {
                                            el.style.fontSize = val;
                                            el.style.marginLeft = val;
                                        } else {
                                            el.style.color = val;
                                        }
                                    }
                                );
                            }

                            if (selectedText === selectedTextBlock && cmd === 'mceApplyTextcolor') {
                                if (this.hasGradientBackground(nodeParent)) {
                                    nodeParent.style.removeProperty('background');
                                    nodeParent.style.removeProperty('-webkit-text-fill-color');
                                    nodeParent.style.removeProperty('-webkit-background-clip');

                                    if (ui === 'hilitecolor') {
                                        nodeParent.style.backgroundColor = val;
                                    }

                                    if (nodeParent.hasAttribute('data-mce-style')) {
                                        let mceStyle = nodeParent.getAttribute('data-mce-style');

                                        mceStyle = mceStyle
                                            .replace(/background:.*?;/g, '')
                                            .replace(/-webkit-text-fill-color:.*?;/g, '')
                                            .replace(/-webkit-background-clip:.*?;/g, '');

                                        if (ui === 'hilitecolor') {
                                            if (!/background-color:.*?;/i.test(mceStyle)) {
                                                mceStyle += `background-color: ${val}; `;
                                            } else {
                                                mceStyle = mceStyle.replace(
                                                    /background-color:.*?;/i,
                                                    `background-color: ${val};`
                                                );
                                            }
                                        }

                                        nodeParent.setAttribute('data-mce-style', mceStyle.trim());

                                        if (!nodeParent.getAttribute('data-mce-style')) {
                                            nodeParent.removeAttribute('data-mce-style');
                                        }
                                    }
                                }
                            }

                            if (this.hasGradientBackground(node) && cmd === 'mceApplyTextcolor') {
                                node.style.removeProperty('background');
                                node.style.removeProperty('-webkit-text-fill-color');
                                node.style.removeProperty('-webkit-background-clip');

                                if (ui === 'hilitecolor') {
                                    node.style.backgroundColor = val;
                                }

                                if (node.hasAttribute('data-mce-style')) {
                                    let mceStyle = node.getAttribute('data-mce-style');

                                    mceStyle = mceStyle
                                        .replace(/background:.*?;/g, '')
                                        .replace(/-webkit-text-fill-color:.*?;/g, '')
                                        .replace(/-webkit-background-clip:.*?;/g, '');

                                    if (ui === 'hilitecolor') {
                                        if (!/background-color:.*?;/i.test(mceStyle)) {
                                            mceStyle += `background-color: ${val}; `;
                                        } else {
                                            mceStyle = mceStyle.replace(
                                                /background-color:.*?;/i,
                                                `background-color: ${val};`
                                            );
                                        }
                                    }

                                    node.setAttribute('data-mce-style', mceStyle.trim());

                                    if (!node.getAttribute('data-mce-style')) {
                                        node.removeAttribute('data-mce-style');
                                    }
                                }
                            }
                        }

                        if (cmd === 'mceToggleFormat') {
                            const node = evt.target.selection.getNode();
                            node.classList = node.classList.value.split(' ').pop();
                        }
                    });
            },
        };

        if (this.placeholder) {
            this.config.placeholder = this.placeholder;
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const { content, bindToElement, selectedThemeColors } = changes;

        if (bindToElement && !bindToElement.firstChange) {
            const editors = document.querySelectorAll('.tox-tinymce');
            const editorToolbarTopPosition = this.bindToElement.getBoundingClientRect().top - this.TOOLBAR_HEIGHT;

            editors.forEach((editor) => {
                this.renderer.setStyle(editor, 'top', `${editorToolbarTopPosition}px`);
            });
        }

        if (selectedThemeColors && this.editor) {
            updateEditorsState(selectedThemeColors.currentValue, this.editor);
        }

        if (!content || !this.editor) {
            return;
        }

        if (this.editor.getContent() !== this.content) {
            this.editor.setContent(this.content ?? '');
        }
    }

    public hasGradientBackground(node) {
        if (!node || !(node instanceof HTMLElement)) {
            return false;
        }
        const computedStyle = window.getComputedStyle(node);
        const background = computedStyle.background || '';

        return Boolean(background.includes('gradient'));
    }

    public get editorId(): string {
        return `${this.containerId}-${this.id}`;
    }

    private emitContentChange(content: string): void {
        if (content === this.lastEmittedContent) {
            return;
        }

        this.lastEmittedContent = content;

        this.changeContent.emit(content);
    }

    private changeEditorHeightHandler(): void {
        const newHeight = this.editor.bodyElement.offsetHeight;
        // const newHeight = this.editor.getContainer().offsetHeight;
        // console.log(newHeight);
        if (newHeight !== this.lastEditorHeight) {
            this.changeEditorHeight.emit(newHeight);
            this.lastEditorHeight = newHeight;
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
